import { CellFocusedEvent, CellPosition, GridApi, TabToNextCellParams } from 'ag-grid-community'
import { getInstance } from 'ag-grid-react'
import { isNil } from 'lodash'

export const focusCellEditor = (params: CellFocusedEvent): void => {
  const rowNode = !isNil(params.rowIndex) && params.api.getDisplayedRowAtIndex(params.rowIndex)

  if (isNil(params.rowIndex) || !rowNode || !params.column) return

  const rendererParams = {
    rowNodes: [rowNode],
    columns: [params.column]
  }

  const [cellRenderer]: any[] = params.api.getCellEditorInstances(rendererParams)

  cellRenderer && getInstance(cellRenderer, comp => comp?.focusIn?.())
}

/**
 * it search the next editable cell, if it doesn't find any on the grid it
 * let's ag grid return the next cell
 */
export const tabToNextEditableCell = (params: TabToNextCellParams): CellPosition | boolean => {
  const isEditable = isCellEditable(params.api)
  const allVisibleCols = params.api.getAllDisplayedColumns()

  if (params.nextCellPosition) {
    let candidate: CellPosition = params.nextCellPosition

    while (!isEditable(candidate)) {
      const nextColumn = params.backwards
        ? params.api.getDisplayedColBefore(candidate.column)
        : params.api.getDisplayedColAfter(candidate.column)

      if (nextColumn) {
        // return next editable cell from row
        candidate = {
          column: nextColumn,
          rowIndex: candidate.rowIndex,
          rowPinned: candidate.rowPinned
        }
      } else {
        const rowIndex = params.backwards ? candidate.rowIndex - 1 : candidate.rowIndex + 1
        const nextRow = params.api.getDisplayedRowAtIndex(rowIndex)

        if (nextRow) {
          // next editable cell from next row
          candidate = {
            column: params.backwards ? allVisibleCols.slice(-1)[0] : allVisibleCols.slice(0)[0],
            rowIndex,
            rowPinned: params.api.getDisplayedRowAtIndex(rowIndex)?.rowPinned
          }
        } else {
          // go back to first or last row and start over
          const rowIndex = params.backwards ? params.api.getLastDisplayedRowIndex() : params.api.getFirstDisplayedRowIndex()
          const row = params.api.getDisplayedRowAtIndex(rowIndex)!
          const visibleEditableCols = allVisibleCols.filter(a => a.isCellEditable(row))

          candidate = {
            column: params.backwards ? visibleEditableCols.slice(-1)[0] : visibleEditableCols.slice(0)[0],
            rowIndex: rowIndex,
            rowPinned: params.api.getDisplayedRowAtIndex(rowIndex)?.rowPinned
          }
        }
        if (!params.api.getDisplayedRowAtIndex(rowIndex)) break
      }
    }

    if (!candidate || !candidate.column || isNil(candidate.rowIndex)) return params.nextCellPosition

    return candidate
  }

  return params.nextCellPosition ?? false
}

const isCellEditable = (api: GridApi) => (cellPosition: CellPosition) => {
  const rowNode = api.getDisplayedRowAtIndex(cellPosition.rowIndex)
  return rowNode && cellPosition.column.isCellEditable(rowNode)
}
